import { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';
import {
  useConnectPsa,
  useDeletePsaConnection,
  usePsaConnections,
  useUpdatePsaConnection,
  type PsaConnection,
  type PsaKind,
  type UpdateConnectionInput,
} from '@/hooks/usePsa.ts';
import { useAuth } from '@/hooks/useAuth.ts';
import { useConfirm } from '@/hooks/useConfirm.tsx';
import { useDivisions } from '@/hooks/useDivisions.ts';
import { usePermission } from '@/hooks/usePermission.ts';
import { apiErrorMessage } from '@/utils/apiError.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx';
import { AutotaskSettings } from './AutotaskSettings.tsx';
import { CustomerMappings } from './CustomerMappings.tsx';
import { HaloSettings } from './HaloSettings.tsx';
import { HaloWebhook } from './HaloWebhook.tsx';

/**
 * The organization's PSA connections (docs/PsaIntegration.md, "Connecting").
 *
 * Connecting, changing settings and mapping customers take `notification_route:manage`
 * and `incident:update`: a connection is a route target, and it acts on incidents,
 * so whoever makes one must be able to do what it does. Reading takes
 * `notification_route:read`. The caller gates the whole section on the
 * `psa_ticketing` rollout.
 */

/** The whole organization, in the scope picker; a division is never named this. */
const WHOLE_ORG = '__org';

const PSA_NAMES: Record<PsaKind, string> = { autotask: 'Autotask', halo: 'Halo' };

function StatusBadge({ connection }: { connection: PsaConnection }) {
  if (connection.status === 'active') return <Badge variant="secondary">Active</Badge>;
  if (connection.status === 'needs_attention') return <Badge variant="destructive">Needs attention</Badge>;
  return <Badge variant="outline">Disconnected</Badge>;
}

export function PsaConnections() {
  const canRead = usePermission('notification_route:read');
  // Both hooks always run: `a() && b()` would skip the second, which hooks forbid.
  const canManageRoutes = usePermission('notification_route:manage');
  const canUpdateIncidents = usePermission('incident:update');
  const canManage = canManageRoutes && canUpdateIncidents;
  const connections = usePsaConnections(canRead);
  const [connecting, setConnecting] = useState(false);

  if (!canRead) {
    return <p className="text-sm text-muted-foreground">You don’t have access to this organization’s PSA connections.</p>;
  }
  if (connections.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (connections.isError) {
    return (
      <p className="text-sm text-destructive">
        {apiErrorMessage(connections.error, 'Couldn’t load the PSA connections.')}
      </p>
    );
  }
  const rows = connections.data ?? [];

  return (
    <div className="space-y-5">
      {rows.length === 0 && !connecting ? (
        <EmptyState
          title="No PSA connected"
          description="Connect Autotask or Halo to open tickets from incidents and keep them in step: notes, acknowledgement, priority and recovery."
          action={
            canManage ? (
              <Button size="sm" onClick={() => { setConnecting(true); }}>
                <Plus className="w-3.5 h-3.5" /> Connect a PSA
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {rows.map((c) => (
        <ConnectionCard key={c.id} connection={c} canManage={canManage} />
      ))}
      {connecting ? (
        <ConnectForm onDone={() => { setConnecting(false); }} />
      ) : rows.length > 0 && canManage ? (
        <Button variant="outline" size="sm" onClick={() => { setConnecting(true); }}>
          <Plus className="w-3.5 h-3.5" /> Connect another
        </Button>
      ) : null}
    </div>
  );
}

function ConnectForm({ onDone }: { onDone: () => void }) {
  const { user } = useAuth();
  const divisions = useDivisions(user?.organizationId);
  const connect = useConnectPsa();
  const [kind, setKind] = useState<PsaKind>('autotask');
  const [scope, setScope] = useState(WHOLE_ORG);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [integrationCode, setIntegrationCode] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [resourceUrl, setResourceUrl] = useState('');
  const [tenant, setTenant] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const ready =
    kind === 'autotask'
      ? username.trim() && secret && integrationCode.trim()
      : authUrl.trim() && resourceUrl.trim() && clientId.trim() && clientSecret;
  const psa = PSA_NAMES[kind];

  return (
    <form
      className="bg-card border border-border rounded-lg p-5 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const common = { name: name.trim() || undefined, divisionId: scope === WHOLE_ORG ? null : scope };
        connect.mutate(
          kind === 'autotask'
            ? { ...common, kind, username: username.trim(), secret, integrationCode: integrationCode.trim() }
            : {
                ...common,
                kind,
                authUrl: authUrl.trim(),
                resourceUrl: resourceUrl.trim(),
                tenant: tenant.trim() || undefined,
                clientId: clientId.trim(),
                clientSecret,
              },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="text-sm font-semibold">Connect a PSA</h3>
      <div className="space-y-1 max-w-xs">
        <Label htmlFor="psa-connect-kind">PSA</Label>
        <Select value={kind} onValueChange={(v) => { setKind(v as PsaKind); }}>
          <SelectTrigger id="psa-connect-kind" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="autotask">Autotask</SelectItem>
            <SelectItem value="halo">HaloPSA</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {kind === 'autotask' ? (
        <p className="text-xs text-muted-foreground">
          Use an Autotask API-only user whose security level can read and write tickets and ticket notes and
          read companies. Witina finds its zone and checks the credentials before saving them.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          In Halo, under Configuration › Integrations › HaloPSA API, add an application that authenticates with a
          client ID and secret, allowed to read and change tickets and actions and to read clients, agents and users.
          The same page’s API details show the authorisation server, resource server and tenant. Witina checks the
          credentials before saving them.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {kind === 'autotask' ? (
          <AutotaskFields username={username} setUsername={setUsername} secret={secret} setSecret={setSecret}
            integrationCode={integrationCode} setIntegrationCode={setIntegrationCode} />
        ) : (
          <>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="psa-connect-auth">Authorisation server</Label>
              <Input id="psa-connect-auth" autoComplete="off" placeholder="https://yourcompany.halopsa.com/auth"
                value={authUrl} onChange={(e) => { setAuthUrl(e.target.value); }} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="psa-connect-resource">Resource server</Label>
              <Input id="psa-connect-resource" autoComplete="off" placeholder="https://yourcompany.halopsa.com/api"
                value={resourceUrl} onChange={(e) => { setResourceUrl(e.target.value); }} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="psa-connect-tenant">Tenant</Label>
              <Input id="psa-connect-tenant" autoComplete="off" value={tenant}
                onChange={(e) => { setTenant(e.target.value); }} />
              <p className="text-xs text-muted-foreground">For a Halo that Halo hosts; leave it empty for your own server.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="psa-connect-client">Client ID</Label>
              <Input id="psa-connect-client" autoComplete="off" value={clientId}
                onChange={(e) => { setClientId(e.target.value); }} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="psa-connect-client-secret">Client secret</Label>
              <Input id="psa-connect-client-secret" type="password" autoComplete="new-password" value={clientSecret}
                onChange={(e) => { setClientSecret(e.target.value); }} />
            </div>
          </>
        )}
        <div className="space-y-1">
          <Label htmlFor="psa-connect-scope">Serves</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger id="psa-connect-scope" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={WHOLE_ORG}>The whole organization</SelectItem>
              {(divisions.data ?? []).map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="psa-connect-name">Name on the timeline</Label>
          <Input id="psa-connect-name" placeholder={psa} value={name}
            onChange={(e) => { setName(e.target.value); }} />
        </div>
      </div>
      {connect.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(connect.error, `Couldn’t connect to ${psa}.`)}</p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!ready || connect.isPending}>
          {connect.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Connect
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

function AutotaskFields({
  username,
  setUsername,
  secret,
  setSecret,
  integrationCode,
  setIntegrationCode,
}: {
  username: string;
  setUsername: (v: string) => void;
  secret: string;
  setSecret: (v: string) => void;
  integrationCode: string;
  setIntegrationCode: (v: string) => void;
}) {
  return (
    <>
      <div className="space-y-1">
        <Label htmlFor="psa-connect-username">API username</Label>
        <Input id="psa-connect-username" autoComplete="off" value={username}
          onChange={(e) => { setUsername(e.target.value); }} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="psa-connect-secret">Secret</Label>
        <Input id="psa-connect-secret" type="password" autoComplete="new-password" value={secret}
          onChange={(e) => { setSecret(e.target.value); }} />
      </div>
      <div className="space-y-1 sm:col-span-2">
        <Label htmlFor="psa-connect-code">API tracking identifier</Label>
        <Input id="psa-connect-code" autoComplete="off" value={integrationCode}
          onChange={(e) => { setIntegrationCode(e.target.value); }} />
        <p className="text-xs text-muted-foreground">
          The API user’s “Custom (Internal Integration)” identifier, from its API Tracking Identifier setting.
        </p>
      </div>
    </>
  );
}

function ConnectionCard({ connection, canManage }: { connection: PsaConnection; canManage: boolean }) {
  const { user } = useAuth();
  const divisions = useDivisions(user?.organizationId);
  const remove = useDeletePsaConnection();
  const { confirm, confirmDialog } = useConfirm();
  const scope = connection.divisionId
    ? (divisions.data?.find((d) => d.id === connection.divisionId)?.name ?? 'One division')
    : 'The whole organization';

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Disconnect ${connection.name}?`,
      message: 'Witina stops talking to it. A connection with tickets bound to incidents can’t be removed.',
      confirmText: 'Disconnect',
      isDanger: true,
    });
    if (ok) remove.mutate(connection.id);
  };

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-4">
      {confirmDialog}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            {connection.name} <StatusBadge connection={connection} />
          </h3>
          <p className="text-xs text-muted-foreground mt-1">{PSA_NAMES[connection.kind]} · serves {scope}</p>
        </div>
        {canManage ? (
          <Button variant="ghost" size="sm" onClick={() => { void handleDelete(); }} disabled={remove.isPending}>
            <Trash2 className="w-3.5 h-3.5" /> Disconnect
          </Button>
        ) : null}
      </header>
      {connection.status === 'needs_attention' && connection.lastError ? (
        <p className="text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {connection.lastError}. Nothing is sent until its credentials are fixed under Credentials.
          </span>
        </p>
      ) : null}
      {remove.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(remove.error, 'Couldn’t disconnect.')}</p>
      ) : null}
      <InstantUpdatesLine connection={connection} />
      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          {canManage ? <TabsTrigger value="settings">Ticket settings</TabsTrigger> : null}
          {canManage ? <TabsTrigger value="automatic">Automatic tickets</TabsTrigger> : null}
          {canManage && connection.kind === 'halo' ? <TabsTrigger value="webhook">Webhook</TabsTrigger> : null}
          {canManage ? <TabsTrigger value="credentials">Credentials</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="customers" className="pt-3">
          <CustomerMappings connection={connection} canManage={canManage} />
        </TabsContent>
        {canManage ? (
          <TabsContent value="settings" className="pt-3">
            {connection.kind === 'halo' ? (
              <HaloSettings connection={connection} />
            ) : (
              <AutotaskSettings connection={connection} />
            )}
          </TabsContent>
        ) : null}
        {canManage ? (
          <TabsContent value="automatic" className="pt-3">
            <AutomaticTickets connection={connection} />
          </TabsContent>
        ) : null}
        {canManage && connection.kind === 'halo' ? (
          <TabsContent value="webhook" className="pt-3">
            <HaloWebhook connection={connection} />
          </TabsContent>
        ) : null}
        {canManage ? (
          <TabsContent value="credentials" className="pt-3">
            <CredentialsForm connection={connection} />
          </TabsContent>
        ) : null}
      </Tabs>
    </section>
  );
}

/**
 * Whether the PSA tells Witina at once when a ticket changes. Off is not broken:
 * Witina still checks every 5 minutes, so the line says what it means for timing.
 */
function InstantUpdatesLine({ connection }: { connection: PsaConnection }) {
  const { state, reason } = connection.instantUpdates;
  if (state === 'on') {
    return <p className="text-xs text-muted-foreground">Changes in {connection.name} reach Witina within a minute.</p>;
  }
  if (state === 'setting_up') {
    return (
      <p className="text-xs text-muted-foreground">
        Setting up instant updates; until then, changes in {connection.name} reach Witina within 5 minutes.
      </p>
    );
  }
  if (state === 'waiting') {
    return (
      <p className="text-xs text-muted-foreground">
        Changes in {connection.name} reach Witina within 5 minutes. Add Witina’s webhook in Halo, under Webhook,
        and they arrive within a minute.
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground">
      Changes in {connection.name} reach Witina within 5 minutes; instant updates are off
      {reason ? `: ${reason}` : ''}. Witina tries again within the hour. Giving the API user “Can create
      WebHooks” turns them on.
    </p>
  );
}

/**
 * When a route makes tickets by itself (docs/PsaIntegration.md, "Creating tickets"):
 * a route to this connection decides which incidents, and the settle delay how long
 * each must stay open first, so an outage that rolls up inside it is one ticket.
 */
function AutomaticTickets({ connection }: { connection: PsaConnection }) {
  const update = useUpdatePsaConnection(connection.id);
  const [minutes, setMinutes] = useState(String(connection.settleMinutes));
  const value = Number(minutes);
  const valid = minutes.trim() !== '' && Number.isInteger(value) && value >= 0 && value <= 1440;
  const id = `psa-${connection.id}-settle`;

  return (
    <form
      className="space-y-3 max-w-lg"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ settleMinutes: value });
      }}
    >
      <p className="text-sm text-muted-foreground">
        A route to {connection.name} under{' '}
        <Link to="/admin/notifications/routing" className="underline">Notification routing</Link> makes a ticket for each
        incident it matches. Each waits out the settle delay first: one that recovers, or rolls up under a broader
        incident, in the meantime gets no ticket. Anyone can still create one by hand straight away.
      </p>
      <div className="space-y-1">
        <Label htmlFor={id}>Settle delay (minutes)</Label>
        <Input id={id} type="number" min={0} max={1440} className="w-32" value={minutes}
          onChange={(e) => { setMinutes(e.target.value); }} />
      </div>
      {update.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(update.error, 'Couldn’t save the delay.')}</p>
      ) : null}
      {update.isSuccess ? <p className="text-sm text-muted-foreground">Saved.</p> : null}
      <Button type="submit" size="sm" disabled={!valid || update.isPending}>
        {update.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Save
      </Button>
    </form>
  );
}

/** The credentials each PSA takes, as the form labels them. */
const CREDENTIAL_FIELDS: Record<PsaKind, { key: keyof UpdateConnectionInput; label: string; secret?: boolean }[]> = {
  autotask: [
    { key: 'username', label: 'API username' },
    { key: 'secret', label: 'Secret', secret: true },
    { key: 'integrationCode', label: 'API tracking identifier' },
  ],
  halo: [
    { key: 'authUrl', label: 'Authorisation server' },
    { key: 'resourceUrl', label: 'Resource server' },
    { key: 'tenant', label: 'Tenant' },
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client secret', secret: true },
  ],
};

/** A rotated secret, another API user or application: checked against the PSA before it's kept. */
function CredentialsForm({ connection }: { connection: PsaConnection }) {
  const update = useUpdatePsaConnection(connection.id);
  const fields = CREDENTIAL_FIELDS[connection.kind];
  const [values, setValues] = useState<Partial<Record<keyof UpdateConnectionInput, string>>>({});
  const id = (name: string) => `psa-${connection.id}-cred-${name}`;
  const any = fields.some((f) => (values[f.key] ?? '').trim());

  return (
    <form
      className="space-y-3 max-w-sm"
      onSubmit={(e) => {
        e.preventDefault();
        // Secrets are sent as typed; everything else trimmed, and left out when empty.
        const changed: UpdateConnectionInput = Object.fromEntries(
          fields
            .map((f) => [f.key, f.secret ? (values[f.key] ?? '') : (values[f.key] ?? '').trim()] as const)
            .filter(([, v]) => v !== ''),
        );
        update.mutate(changed, { onSuccess: () => { setValues({}); } });
      }}
    >
      <p className="text-xs text-muted-foreground">
        Fill in only what changed. The stored values are never shown.
      </p>
      {fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <Label htmlFor={id(f.key)}>{f.label}</Label>
          <Input id={id(f.key)} type={f.secret ? 'password' : 'text'}
            autoComplete={f.secret ? 'new-password' : 'off'} value={values[f.key] ?? ''}
            onChange={(e) => { setValues((v) => ({ ...v, [f.key]: e.target.value })); }} />
        </div>
      ))}
      {update.isError ? (
        <p className="text-sm text-destructive">
          {apiErrorMessage(update.error, `${PSA_NAMES[connection.kind]} refused those credentials.`)}
        </p>
      ) : null}
      {update.isSuccess ? <p className="text-sm text-muted-foreground">Updated and checked.</p> : null}
      <Button type="submit" size="sm" disabled={!any || update.isPending}>
        {update.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Update credentials
      </Button>
    </form>
  );
}
